import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@jourfx/ui";
import { ArrowLeft, Copy } from "lucide-react";

type Id<T extends string> = string & { __tableName: T };

interface VersichertenDetailProps {
  versicherteId: string;
}

export function VersichertenDetail({ versicherteId }: VersichertenDetailProps) {
  const { t } = useTranslation("versicherte");
  const apiRef = api as any;
  const profile = useQuery(apiRef.versichertenProfiles.index.get, {
    versichertenProfileId: versicherteId as Id<"versichertenProfiles">,
  }) as
    | {
        _id: string;
        firstName: string;
        lastName: string;
        email?: string;
        phone?: string;
        dateOfBirth?: string;
        versichertennummer?: string;
        begutachtungsGrund?: string;
        registrationSource?: "internal" | "self_service";
        registrationSubmittedAt?: number;
        einwilligungVideoAcceptedAt?: number;
        einwilligungVideoUrl?: string;
        art13DsgvoAcceptedAt?: number;
        art13DsgvoUrl?: string;
        notes?: string;
        accessCode?: string;
        assignedGutachterId?: string;
        assignedTemplateId?: string;
        invitations: Array<{
          _id: string;
          status: string;
          createdAt: number;
          gutachterId: string;
          gutachterName?: string;
          templateId?: string;
          templateName?: string;
          duration: number;
          appointmentId?: string;
        }>;
        callbackEntries: Array<{
          _id: string;
          createdAt: number;
          preferredDateStart?: number;
          preferredDateEnd?: number;
          notes?: string;
        }>;
      }
    | null
    | undefined;

  const gutachterList = useQuery(api.appointments.index.listGutachter) as
    | Array<{ _id: string; name: string }>
    | undefined;
  const templates = useQuery(api.templates.index.list) as
    | Array<{ _id: string; name: string; duration: number }>
    | undefined;

  const updateProfile = useMutation(apiRef.versichertenProfiles.index.update);
  const createInvitation = useMutation(apiRef.consultationInvitations.index.create);
  const generateCredentials = useMutation(apiRef.versichertenProfiles.index.generateCredentials);

  const [selectedGutachterId, setSelectedGutachterId] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingCredentials, setIsGeneratingCredentials] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState<{ accessCode: string } | null>(null);
  const [invitationCreated, setInvitationCreated] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [invitationEmailScheduled, setInvitationEmailScheduled] = useState<boolean | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const currentInvitation = useMemo(
    () => profile?.invitations?.[0] ?? null,
    [profile?.invitations]
  );

  const selectedTemplate = useMemo(
    () => templates?.find((template) => template._id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId]
  );

  useEffect(() => {
    if (!profile) return;
    const fallbackGutachterId = profile.assignedGutachterId || currentInvitation?.gutachterId || "";
    const fallbackTemplateId = profile.assignedTemplateId || currentInvitation?.templateId || "";
    setSelectedGutachterId((previous) => previous || fallbackGutachterId);
    setSelectedTemplateId((previous) => previous || fallbackTemplateId);
  }, [
    currentInvitation?.gutachterId,
    currentInvitation?.templateId,
    profile,
  ]);

  if (profile === undefined) {
    return <p className="text-muted-foreground">Lade Versichertendaten...</p>;
  }

  if (!profile) {
    return (
      <div className="space-y-4">
        <Link to="/versicherte" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
          Zurück zu Versicherte
        </Link>
        <p>Versichertenprofil nicht gefunden.</p>
      </div>
    );
  }

  const formatOptionalDateTime = (value?: number) =>
    value ? new Date(value).toLocaleString("de-DE") : "Nicht bestätigt";

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await updateProfile({
        versichertenProfileId: versicherteId as Id<"versichertenProfiles">,
        patch: {
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          assignedGutachterId: selectedGutachterId || undefined,
          assignedTemplateId: selectedTemplateId || undefined,
        },
      });
    } catch (error) {
      console.error("Failed to save versicherten profile", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAssignment = async () => {
    setIsSaving(true);
    try {
      await updateProfile({
        versichertenProfileId: versicherteId as Id<"versichertenProfiles">,
        patch: {
          assignedGutachterId: selectedGutachterId || undefined,
          assignedTemplateId: selectedTemplateId || undefined,
        },
      });
    } catch (error) {
      console.error("Failed to save assignment", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateInvitation = async () => {
    if (!selectedGutachterId || !selectedTemplateId) return;
    setInvitationError(null);
    setInvitationCreated(false);
    setInvitationEmailScheduled(null);
    setIsSaving(true);
    try {
      await updateProfile({
        versichertenProfileId: versicherteId as Id<"versichertenProfiles">,
        patch: {
          assignedGutachterId: selectedGutachterId as Id<"users">,
          assignedTemplateId: selectedTemplateId as Id<"appointmentTemplates">,
        },
      });

      const result = await createInvitation({
        versichertenProfileId: versicherteId as Id<"versichertenProfiles">,
        gutachterId: selectedGutachterId as Id<"users">,
        templateId: selectedTemplateId as Id<"appointmentTemplates">,
      });
      setInvitationCreated(true);
      setInvitationEmailScheduled(Boolean((result as { emailScheduled?: boolean } | null)?.emailScheduled));
    } catch (error) {
      console.error("Failed to create consultation invitation", error);
      setInvitationError(
        error instanceof Error && error.message
          ? error.message
          : "Einladung konnte nicht erstellt werden."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateCredentials = async () => {
    setIsGeneratingCredentials(true);
    try {
      const result = await generateCredentials({ versichertenProfileId: versicherteId as Id<"versichertenProfiles"> });
      setGeneratedCredentials({ accessCode: result.accessCode });
    } catch (error) {
      console.error("Failed to generate credentials", error);
    } finally {
      setIsGeneratingCredentials(false);
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/versicherte" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />
        Zurück zu Versicherte
      </Link>

      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {profile.firstName} {profile.lastName}
          </h1>
          <p className="text-sm text-muted-foreground">
            {profile.registrationSource === "self_service" ? "Selbstregistriert" : "Intern erfasst"}
            {profile.dateOfBirth && ` · Geb. ${profile.dateOfBirth}`}
          </p>
        </div>
        {currentInvitation && (
          <Badge variant={currentInvitation.status === "open" ? "outline" : "secondary"}>
            Einladung: {currentInvitation.status}
          </Badge>
        )}
      </div>

      {/* ── Credentials + Profile: side-by-side on desktop ── */}
      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">{t("credentials.title")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {profile.accessCode ? (
              <>
                <div className="space-y-2">
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <span className="text-sm text-muted-foreground">{t("credentials.accessCode")}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold">{profile.accessCode}</span>
                      <Button variant="ghost" size="sm" onClick={() => navigator.clipboard.writeText(profile.accessCode!)}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{t("credentials.hint")}</p>
              </>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Noch keine Zugangsdaten vorhanden.
                </p>
                {generatedCredentials ? (
                  <div className="rounded-md bg-green-50 border border-green-200 p-3 space-y-2">
                    <p className="text-sm font-medium text-green-800">Zugangsdaten erstellt!</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-700">{t("credentials.accessCode")}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold">{generatedCredentials.accessCode}</span>
                        <Button variant="ghost" size="sm" onClick={() => navigator.clipboard.writeText(generatedCredentials.accessCode)}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-green-600">{t("credentials.hint")}</p>
                  </div>
                ) : (
                  <Button onClick={handleGenerateCredentials} disabled={isGeneratingCredentials}>
                    Zugangsdaten erstellen
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base">Versichertenprofil</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Vorname</Label>
                <Input value={firstName || profile.firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Nachname</Label>
                <Input value={lastName || profile.lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>E-Mail</Label>
                <Input value={email || profile.email || ""} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Telefon</Label>
                <Input value={phone || profile.phone || ""} onChange={(e) => setPhone(e.target.value)} />
              </div>
            </div>
            <Button onClick={handleSaveProfile} disabled={isSaving} size="sm">
              Profil speichern
            </Button>

            {(profile.registrationSource === "self_service" ||
              profile.begutachtungsGrund ||
              profile.einwilligungVideoAcceptedAt ||
              profile.art13DsgvoAcceptedAt) && (
              <details className="mt-2 rounded-md border border-muted bg-muted/20 p-4">
                <summary className="text-sm font-semibold cursor-pointer">
                  Selbstregistrierung — Details
                </summary>
                <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
                  <p>
                    <span className="font-medium">Eingang:</span>{" "}
                    {profile.registrationSubmittedAt
                      ? new Date(profile.registrationSubmittedAt).toLocaleString("de-DE")
                      : "-"}
                  </p>
                  <p>
                    <span className="font-medium">Begutachtungsgrund:</span>{" "}
                    {profile.begutachtungsGrund || "-"}
                  </p>
                  <p>
                    <span className="font-medium">Einwilligung:</span>{" "}
                    {formatOptionalDateTime(profile.einwilligungVideoAcceptedAt)}
                  </p>
                  <p>
                    <span className="font-medium">Art. 13 DSGVO:</span>{" "}
                    {formatOptionalDateTime(profile.art13DsgvoAcceptedAt)}
                  </p>
                </div>
                <div className="mt-2 flex flex-wrap gap-3 text-sm">
                  {profile.einwilligungVideoUrl && (
                    <a href={profile.einwilligungVideoUrl} target="_blank" rel="noreferrer" className="text-primary underline">
                      Einwilligungserklärung
                    </a>
                  )}
                  {profile.art13DsgvoUrl && (
                    <a href={profile.art13DsgvoUrl} target="_blank" rel="noreferrer" className="text-primary underline">
                      Hinweise Art. 13 DSGVO
                    </a>
                  )}
                </div>
              </details>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Assignment + Invitation: merged into one card ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Einladung vorbereiten</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Gutachter</Label>
              <select
                value={selectedGutachterId}
                onChange={(e) => setSelectedGutachterId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                <option value="">Bitte wählen...</option>
                {gutachterList?.map((g) => (
                  <option key={g._id} value={g._id}>{g.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Begutachtungsanlass (Vorlage)</Label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                <option value="">Bitte wählen...</option>
                {templates?.map((tmpl) => (
                  <option key={tmpl._id} value={tmpl._id}>{tmpl.name} ({tmpl.duration} Min.)</option>
                ))}
              </select>
            </div>
          </div>

          {selectedTemplate && (
            <p className="text-sm text-muted-foreground">
              Dauer: {selectedTemplate.duration} Minuten
            </p>
          )}

          {templates?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Keine aktiven Vorlagen verfügbar. Bitte im Admin zuerst einen Begutachtungsanlass anlegen.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={handleCreateInvitation}
              disabled={isSaving || !selectedGutachterId || !selectedTemplateId}
            >
              Einladung erstellen
            </Button>
            <Button
              variant="outline"
              onClick={handleSaveAssignment}
              disabled={isSaving || !selectedGutachterId}
              size="sm"
            >
              Nur Zuweisung speichern
            </Button>
          </div>

          {invitationError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-sm font-medium text-red-800">
                Einladung konnte nicht erstellt werden: {invitationError}
              </p>
            </div>
          )}

          {invitationCreated && (
            <div className="rounded-md border border-green-200 bg-green-50 p-3">
              <p className="text-sm font-medium text-green-800">
                Einladung erstellt.
              </p>
              <p className="text-sm text-green-700 mt-1">
                {invitationEmailScheduled
                  ? "Einladungs-E-Mail wurde versendet."
                  : "Keine Einladungs-E-Mail versendet (bitte E-Mail-Adresse prüfen)."}
              </p>
              <Button variant="ghost" size="sm" onClick={() => setInvitationCreated(false)} className="mt-1 text-green-700 hover:text-green-900">
                Ausblenden
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── History: Einladungen + Rückrufwünsche side by side ── */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Einladungen
              {profile.invitations.length > 0 && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  ({profile.invitations.length})
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {profile.invitations.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch keine Einladungen.</p>
            ) : (
              profile.invitations.map((inv) => (
                <div key={inv._id} className="flex items-start justify-between border-b last:border-0 py-2.5 first:pt-0">
                  <div>
                    <p className="text-sm font-medium">{new Date(inv.createdAt).toLocaleString("de-DE")}</p>
                    <p className="text-xs text-muted-foreground">
                      {inv.gutachterName || "Gutachter n/a"}
                      {inv.templateName ? ` · ${inv.templateName}` : ` · ${inv.duration} Min.`}
                    </p>
                  </div>
                  <Badge variant={inv.status === "open" ? "outline" : "secondary"} className="shrink-0">
                    {inv.status}
                  </Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Rückrufwünsche
              {profile.callbackEntries.length > 0 && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  ({profile.callbackEntries.length})
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {profile.callbackEntries.length === 0 ? (
              <p className="text-sm text-muted-foreground">Keine Rückrufwünsche.</p>
            ) : (
              profile.callbackEntries.map((entry) => (
                <div key={entry._id} className="border-b last:border-0 py-2.5 first:pt-0 text-sm">
                  <p className="font-medium">{new Date(entry.createdAt).toLocaleString("de-DE")}</p>
                  {entry.preferredDateStart && (
                    <p className="text-xs text-muted-foreground">
                      Wunschzeit: {new Date(entry.preferredDateStart).toLocaleString("de-DE")}
                      {entry.preferredDateEnd ? ` – ${new Date(entry.preferredDateEnd).toLocaleString("de-DE")}` : ""}
                    </p>
                  )}
                  {entry.notes && <p className="text-muted-foreground">{entry.notes}</p>}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
